import Yup from '~/util/yup';
import {
	addStructuredWoundOrderOptions,
	addUnstructuredWoundOrderOptions,
	editStructuredWoundOrderOptions,
	editUnstructuredWoundOrderOptions,
	getWoundsOptions,
	WoundOrderComponentTypeOptions,
	WoundStatusEnum,
	type FullOrderViewModel,
	type WoundOrderComponentViewModel,
	type WoundOrderViewModel
} from '@generated';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { FormModal } from '~/shared/forms/FormModal';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { useSuspenseQuery } from '@tanstack/react-query';
import { AutocompleteSearchFormField } from '~/shared/forms/controls/fields/AutocompleteSearchFormField';
import { Button } from '~/shared/components/buttons/Button';
import type { FormHelpers } from '~/shared/forms/types';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { useState } from 'react';
import { SingleButtonGroupFormField } from '~/shared/forms/controls/fields/button-groups/SingleButtonGroupFormField';
import { useGetWoundOrders } from '../queries';

const woundOrderTypes = [
	{ id: '1', label: 'Structured' },
	{ id: '2', label: 'Unstructured' }
];

const validationSchema = Yup.object().shape({
	existingWoundId: Yup.string().label('Existing Wound').nullable(),
	preidentificationWoundDetails: Yup.string()
		.label('Wound Details (location, type, size, etc.)')
		.nullable()
		.when('existingWoundId', {
			is: (existingWoundId: string) => existingWoundId === '0',
			then: schema => schema.required()
		}),
	woundOrderType: Yup.string().label('Wound Order Type').nullable().required(),
	frequency: Yup.string()
		.label('Frequency')
		.nullable()
		.when('woundOrderType', {
			is: (woundOrderType: string) => woundOrderType === '1',
			then: schema => schema.required()
		})
		.required(),
	orderComponents: Yup.array()
		.of(
			Yup.object().shape({
				woundOrderComponentTypeId: Yup.number().label('Component Type').nullable().required(),
				product: Yup.string().label('Product').nullable().required(),
				supplyId: Yup.string().label('Supply').nullable(),
				notes: Yup.string().label('Notes').nullable()
			})
		)
		.when('woundOrderType', {
			is: (woundOrderType: string) => woundOrderType === '1',
			then: schema => schema.min(1, 'At least one order component is required')
		}),
	prestructuredCareInstructions: Yup.string()
		.label('Wound Care Instructions')
		.nullable()
		.when('woundOrderType', {
			is: (woundOrderType: string) => woundOrderType === '2',
			then: schema => schema.required()
		})
});

type NewOrderComponentModel = {
	id: string;
	woundOrderComponentTypeId?: number;
	product?: string;
	supplyId?: string;
	frequency?: string;
	notes?: string;
};

function getMutation(woundOrder?: WoundOrderViewModel, selectedWoundOrderType?: string | null) {
	if (woundOrder) {
		return !woundOrder.isPrestructuredCare ? editStructuredWoundOrderOptions : editUnstructuredWoundOrderOptions;
	}
	return selectedWoundOrderType === '1' ? addStructuredWoundOrderOptions : addUnstructuredWoundOrderOptions;
}

function getInitialOrderComponents() {
	return WoundOrderComponentTypeOptions.map(x => ({
		id: window.crypto.randomUUID(),
		woundOrderComponentTypeId: x.id
	}));
}

function getInitialValues(orderId: string, woundOrder?: WoundOrderViewModel) {
	if (woundOrder) {
		return !woundOrder.isPrestructuredCare
			? {
					frequency: woundOrder.frequency,
					orderComponents: woundOrder.orderComponents,
					woundOrderType: '1'
			  }
			: {
					prestructuredCareInstructions: woundOrder.prestructuredCareInstructions
			  };
	}
	return {
		orderId,
		existingWoundId: null,
		preidentificationWoundDetails: null,
		prestructuredCareInstructions: null,
		frequency: null,
		orderComponents: getInitialOrderComponents()
	};
}

export function WoundOrderFormModal({
	fullOrder,
	woundOrder,
	closeModal
}: {
	fullOrder: FullOrderViewModel;
	woundOrder?: WoundOrderViewModel;
	closeModal: () => void;
}) {
	const { data: wounds } = useSuspenseQuery(getWoundsOptions(fullOrder.stayId));
	const { refetch } = useSuspenseQuery(useGetWoundOrders(fullOrder.id));
	const [selectedWoundOrderType, setSelectedWoundOrderType] = useState<string | null>(
		!woundOrder ? null : woundOrder.isPrestructuredCare ? '2' : '1'
	);
	const onSelectedWoundOrderTypeChange = (value: string, formHelpers: FormHelpers) => {
		setSelectedWoundOrderType(value);
		if (value === '1') {
			formHelpers.setFieldValue('prestructuredCareInstructions', null);
			formHelpers.setFieldValue('orderComponents', getInitialOrderComponents());
		} else {
			formHelpers.setFieldValue('prestructuredCareInstructions', null);
			formHelpers.setFieldValue('orderComponents', []);
		}
	};
	const woundOptions = [
		{ id: '0', name: 'New Wound' },
		...wounds.wounds.filter(x => x.woundStatusId !== WoundStatusEnum.Drafted).map(wound => ({ id: wound.id, name: wound.woundLabel }))
	];

	const mutation = getMutation(woundOrder, selectedWoundOrderType);

	const initialValues = getInitialValues(fullOrder.id, woundOrder);

	const removeItem = (id: string, formHelpers: FormHelpers) => {
		formHelpers.setFieldValue(
			'orderComponents',
			formHelpers.values.orderComponents.filter((components: NewOrderComponentModel) => components.id !== id)
		);
	};

	const addItem = (formHelpers: FormHelpers) => {
		const newItem: NewOrderComponentModel = {
			id: window.crypto.randomUUID()
		};
		formHelpers.setFieldValue('orderComponents', [...formHelpers.values.orderComponents, newItem]);
	};

	return (
		<FormModal
			title={woundOrder ? 'Edit Wound Order' : 'New Wound Order'}
			initialValues={initialValues}
			saveMutation={mutation}
			validationSchema={validationSchema}
			onSuccess={() => {
				refetch();
				closeModal();
			}}
			closeModal={closeModal}
			onSaveTransform={values => ({
				...values,
				woundOrderId: woundOrder?.id,
				existingWoundId: values.existingWoundId === '0' ? null : values.existingWoundId
			})}
			size="xl"
		>
			{formHelpers => {
				const components = formHelpers.values.orderComponents;
				return (
					<div className="flex flex-col gap-4">
						<div className="flex w-full max-w-3xl flex-col gap-4">
							{!woundOrder ? (
								<SelectFormField options={woundOptions} name="existingWoundId" />
							) : (
								<div className="flex flex-col gap-1">
									<p className="font-medium text-gray-700">Wound</p>
									<p className="py-2.5 text-sm text-gray-900">{woundOrder.wound.woundLabel}</p>
								</div>
							)}
							{formHelpers.values.existingWoundId === '0' && (
								<div className="col-span-1">
									<TextAreaFormField name="preidentificationWoundDetails" />
								</div>
							)}
							<TextAreaFormField name="frequency" />
							{!woundOrder && (
								<SingleButtonGroupFormField
									options={woundOrderTypes}
									name="woundOrderType"
									onChangePassThrough={value => onSelectedWoundOrderTypeChange(value, formHelpers)}
								/>
							)}
						</div>
						{selectedWoundOrderType === '1' ? (
							<>
								<p className="mt-2 font-medium text-gray-900">Order Components</p>
								{components.map((item: WoundOrderComponentViewModel, index: number) => {
									const componentTypeId = formHelpers.values.orderComponents[index].woundOrderComponentTypeId;
									const selectedComponentType = componentTypeId
										? WoundOrderComponentTypeOptions.find(x => x.id === Number(componentTypeId))
										: null;
									return (
										<div key={item.id} className="rounded-lg border">
											<div className="flex items-center justify-between gap-4 rounded-t-lg border-b bg-gray-50 px-4 py-2">
												<p className="font-medium text-gray-900">
													{selectedComponentType ? selectedComponentType.label : 'Component'}
												</p>
												<Button
													actionType="delete"
													appearance="outline"
													intent="destructive"
													isDisabled={components.length === 1}
													onClick={() => removeItem(item.id, formHelpers)}
													aria-label="Remove component"
												/>
											</div>
											<div className="flex flex-row gap-4 p-4">
												<SelectFormField
													name={`orderComponents[${index}].woundOrderComponentTypeId`}
													options={WoundOrderComponentTypeOptions}
												/>
												<TextFormField name={`orderComponents[${index}].product`} />
												<AutocompleteSearchFormField name={`orderComponents[${index}].supplyId`} searchType="supply" />
												<TextFormField name={`orderComponents[${index}].notes`} />
											</div>
										</div>
									);
								})}
								<div className="flex justify-end">
									<Button actionType="add" onClick={() => addItem(formHelpers)} label="Add Component" />
								</div>
							</>
						) : selectedWoundOrderType === '2' ? (
							<TextAreaFormField name="prestructuredCareInstructions" />
						) : null}
					</div>
				);
			}}
		</FormModal>
	);
}
